/**
 * SceneManager — Mounts the R3F Canvas into a container element.
 *
 * Responsibilities:
 * - React Root → R3F <Canvas> with correct defaults
 * - ResizeObserver for responsive resizing
 * - WebGL context-loss / restore handling
 * - Basic lighting setup (ambient + directional)
 * - Ground plane with shadow material
 * - Cleanup: React unmount, WebGL dispose, ResizeObserver disconnect
 *
 * @see docs/ims-engine-api/01-architecture.md — Core Layer
 */
/* eslint-disable react-refresh/only-export-components -- internal engine file: mixes SceneManager class with helper R3F components (ContextLossHandler, SceneRoot). Not a user-facing component module. */

import { useEffect, useCallback, type ReactNode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { Canvas, useThree } from '@react-three/fiber';
import {
  PCFSoftShadowMap,
  SRGBColorSpace,
  type WebGLRenderer,
  type Scene,
  type Camera,
} from 'three';

import type { EngineError } from '../api/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SceneManagerOptions {
  /** DOM element to mount the canvas into. */
  container: HTMLElement;
  /** Children rendered inside the R3F Canvas. */
  children?: ReactNode;
  /** Callback when WebGL context is lost. */
  onContextLost?: () => void;
  /** Callback when WebGL context is restored. */
  onContextRestored?: () => void;
  /** Callback for errors. */
  onError?: (error: EngineError) => void;
  /** Callback when the canvas is created (exposes gl, scene, camera for external use). */
  onCreated?: (gl: WebGLRenderer, scene: Scene, camera: Camera) => void;
}

// ---------------------------------------------------------------------------
// Internal React Components
// ---------------------------------------------------------------------------

/**
 * Listens for WebGL context loss/restore and notifies via callbacks.
 */
function ContextLossHandler({
  onContextLost,
  onContextRestored,
}: {
  onContextLost?: () => void;
  onContextRestored?: () => void;
}) {
  const gl = useThree((s) => s.gl);

  useEffect(() => {
    const canvas = gl.domElement;
    const handleLost = (event: Event) => {
      event.preventDefault();
      onContextLost?.();
    };
    const handleRestored = () => {
      onContextRestored?.();
    };

    canvas.addEventListener('webglcontextlost', handleLost);
    canvas.addEventListener('webglcontextrestored', handleRestored);
    return () => {
      canvas.removeEventListener('webglcontextlost', handleLost);
      canvas.removeEventListener('webglcontextrestored', handleRestored);
    };
  }, [gl, onContextLost, onContextRestored]);

  return null;
}

// ---------------------------------------------------------------------------
// SceneRoot — The main R3F tree
// ---------------------------------------------------------------------------

interface SceneRootProps {
  children?: ReactNode;
  onContextLost?: () => void;
  onContextRestored?: () => void;
  onCreated?: (gl: WebGLRenderer, scene: Scene, camera: Camera) => void;
}

function SceneRoot({ children, onContextLost, onContextRestored, onCreated }: SceneRootProps) {
  const onCanvasCreated = useCallback(
    (state: { gl: WebGLRenderer; scene: Scene; camera: Camera }) => {
      const { gl, scene, camera } = state;
      // Tone mapping + exposure are now managed by ViewerSettingsManager
      // via PostFxPipeline (which also takes over the render loop at priority 1).
      gl.outputColorSpace = SRGBColorSpace;
      onCreated?.(gl, scene, camera);
    },
    [onCreated],
  );

  return (
    <Canvas
      shadows={{ type: PCFSoftShadowMap }}
      // Force DPR=2 from the very first frame for full supersampling quality.
      // On 1x displays this renders at 4× the pixel count before downsampling,
      // which is essential for resolving fine details (LED clusters, logos).
      // Must match the cinematic preset's dpr:2 to avoid PostFxPipeline reallocation.
      dpr={[2, 2]}
      gl={{
        antialias: false,
        alpha: true,
        powerPreference: 'high-performance',
        // stencil buffer is not used anywhere in this codebase — disabling it
        // reduces the per-framebuffer memory footprint with zero visual impact.
        stencil: false,
        depth: true,
      }}
      camera={{
        fov: 45,
        near: 0.1,
        far: 500,
        position: [6, 2, 6],
      }}
      // Only render when invalidate() is called — saves ~95% GPU budget
      // in idle state. Every component that changes the scene must call
      // invalidate() to request a new frame.
      frameloop="demand"
      // Disable R3F's built-in event system (raycasting on every pointermove).
      // Automotive GLBs have 100-500+ meshes — raycasting all of them per
      // pointer event causes 400ms+ Long Tasks and visible frame drops.
      // Click interaction is handled via a single native DOM raycast instead.
      events={() => ({ enabled: false, priority: 0 })}
      onCreated={onCanvasCreated}
      style={{ width: '100%', height: '100%' }}
    >
      <ContextLossHandler onContextLost={onContextLost} onContextRestored={onContextRestored} />
      {children}
    </Canvas>
  );
}

// ---------------------------------------------------------------------------
// SceneManager (imperative API for non-React callers)
// ---------------------------------------------------------------------------

export class SceneManager {
  private _container: HTMLElement;
  /** Dedicated wrapper div owned exclusively by the engine's React root. */
  private _engineDiv: HTMLDivElement | null = null;
  private _root: Root | null = null;
  private _resizeObserver: ResizeObserver | null = null;
  private _disposed = false;
  private _gl: WebGLRenderer | null = null;
  private _scene: Scene | null = null;
  private _camera: Camera | null = null;

  constructor(private _options: SceneManagerOptions) {
    this._container = _options.container;
  }

  /**
   * Mount the R3F Canvas into the container.
   * Creates a dedicated child div so the engine's React root does not
   * conflict with an outer React tree that owns the container element.
   * Call once — subsequent calls are no-ops.
   */
  mount(children?: ReactNode): void {
    if (this._disposed || this._root) return;

    // Validate WebGL2 support
    const testCanvas = document.createElement('canvas');
    const ctx = testCanvas.getContext('webgl2');
    if (!ctx) {
      this._options.onError?.({
        code: 'WEBGL_NOT_SUPPORTED',
        message: 'WebGL2 is not supported by this browser.',
      });
      return;
    }

    // Create a dedicated child element for the engine's React root.
    // This avoids `removeChild` crashes when the outer React tree also
    // renders children into the same container.
    this._engineDiv = document.createElement('div');
    this._engineDiv.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;';
    this._container.appendChild(this._engineDiv);

    // Create React root on the engine-owned div (not the container itself)
    this._root = createRoot(this._engineDiv);
    this._root.render(
      <SceneRoot
        onContextLost={this._options.onContextLost}
        onContextRestored={this._options.onContextRestored}
        onCreated={(gl, scene, camera) => {
          this._gl = gl;
          this._scene = scene;
          this._camera = camera;
          this._options.onCreated?.(gl, scene, camera);
        }}
      >
        {children ?? this._options.children}
      </SceneRoot>,
    );

    // ResizeObserver for responsive canvas
    this._resizeObserver = new ResizeObserver(() => {
      this.resize();
    });
    this._resizeObserver.observe(this._container);
  }

  /** Re-render with new children (used by createEngine to inject model, camera, etc.). */
  update(children: ReactNode): void {
    if (this._disposed || !this._root) return;
    this._root.render(
      <SceneRoot
        onContextLost={this._options.onContextLost}
        onContextRestored={this._options.onContextRestored}
        onCreated={(gl, scene, camera) => {
          this._gl = gl;
          this._scene = scene;
          this._camera = camera;
          this._options.onCreated?.(gl, scene, camera);
        }}
      >
        {children}
      </SceneRoot>,
    );
  }

  /** Trigger a canvas resize (reads container dimensions). */
  resize(): void {
    // R3F handles resize internally via its own ResizeObserver on the canvas
    // parent. Our ResizeObserver mainly ensures the container layout triggers
    // R3F's internal handler. For explicit resize, we dispatch a resize event.
    if (this._gl) {
      const { width, height } = this._container.getBoundingClientRect();
      this._gl.setSize(width, height, false);
    }
  }

  /** Get the WebGL renderer (available after mount + canvas creation). */
  get gl(): WebGLRenderer | null {
    return this._gl;
  }

  /** Get the Three.js scene (available after mount). */
  get scene(): Scene | null {
    return this._scene;
  }

  /** Get the active camera (available after mount). */
  get camera(): Camera | null {
    return this._camera;
  }

  /** Whether the scene has been mounted. */
  get isMounted(): boolean {
    return this._root !== null && !this._disposed;
  }

  /** Dispose all resources: unmount React, dispose WebGL, disconnect ResizeObserver. */
  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;

    // Disconnect resize observer
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;

    // Unmount React tree (which unmounts R3F and disposes Three.js)
    this._root?.unmount();
    this._root = null;

    // Remove the engine-owned div from the container
    if (this._engineDiv && this._engineDiv.parentNode) {
      this._engineDiv.parentNode.removeChild(this._engineDiv);
    }
    this._engineDiv = null;

    // Dispose WebGL renderer explicitly
    this._gl?.dispose();
    this._gl = null;
    this._scene = null;
    this._camera = null;
  }
}
